import type { RefObject } from 'preact';

import type { IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import type { Params as AutoSyncVisualizationParams } from 'Components/fitmentSearch/autoSyncVisualization';
import FacetToggle, {
  type FacetToggleConfig,
  type Params as FacetToggleParams,
} from 'Components/smallComponents/facetToggle';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { discardVehicleFieldsRequested, isolatedDiscardVehicle } from 'Core/actions/fitmentSearch/index';
import { discardField } from 'Core/actions/request';
import {
  createResponseVehicleSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  fitmentSearchFieldsSelector,
  isVehicleSelectedSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';

import type { VehicleTemplateData } from '../types';

interface Props {
  template: TemplateFunction<VehicleLabelTemplateParams>;
  appendedClasses?: string | (string | false | undefined)[];
  rootRef: RefObject<HTMLElement>;
  isolatedKey: IsolatedKey;
  onChangeVehicle: () => void;
  openDialog: () => void;
  facetToggleConfig?: FacetToggleConfig;
  globalVehicleDiscardEnabled?: boolean;
  templateName: string;
  autoSyncVisualization?: TemplateFunctionInvoker<AutoSyncVisualizationParams>;
}

export interface VehicleLabelTemplateParams extends VehicleTemplateData {
  template: string;
  isVehicleSelected: boolean;
  fitmentSearchTitle: string;
  changeVehicle: () => void;
  discardVehicle: () => void;
  discardFields: (fields: string[]) => void;
  facetToggle: TemplateFunctionInvoker<FacetToggleParams>;
  openDialog: () => void;
  autoSyncVisualization?: TemplateFunctionInvoker<AutoSyncVisualizationParams>;
}

export default function VehicleLabel({
  template,
  appendedClasses,
  rootRef,
  isolatedKey,
  onChangeVehicle,
  openDialog,
  facetToggleConfig,
  globalVehicleDiscardEnabled,
  templateName,
  autoSyncVisualization,
}: Props): TemplateResult {
  const dispatch = useDispatch();

  const selectedVehicle = useSelector(createResponseVehicleSelector(isolatedKey));
  const isVehicleSelected = useSelector(isVehicleSelectedSelector);
  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const discardVehicle = () => {
    dispatch(discardField('Universal', { stayOnPage: true, sendRequest: false }));
    dispatch(isolatedDiscardVehicle(isolatedKey, globalVehicleDiscardEnabled));
    onChangeVehicle();
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const discardFields = (fields: string[]) =>
    dispatch(discardVehicleFieldsRequested(isolatedKey, fitmentFields, fields));

  const facetToggle = (templ: TemplateFunction<FacetToggleParams>) => {
    if (!facetToggleConfig) {
      return null;
    }

    const props = {
      template: templ,
      key: 'facet-toggle',
      ...facetToggleConfig,
    };

    return <FacetToggle {...props} />;
  };

  const params = {
    template: templateName,
    isVehicleSelected,
    fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
    vehicle: selectedVehicle.filteredFieldMap,
    vehicleString: selectedVehicle.toString(),
    vehicleBaseValues: selectedVehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: selectedVehicle.getFilteredValues(extraFitmentFields),
    changeVehicle: onChangeVehicle,
    discardVehicle,
    discardFields,
    facetToggle,
    openDialog,
    autoSyncVisualization,
  };
  const component = template.call(params);

  return cloneSafe(component, rootRef, { appendedClasses });
}
